import { createFileRoute, useRouter } from "@tanstack/react-router";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { ApiCustomer } from "src/api/api_customer";
import TablePagination from "src/components/table-pagination";
import WS from "src/api/ws";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link PendingSubnetsOverview}
 */
export type PendingSubnetsOverviewProps = {};

const LIMIT = 25;

/**
 * Overview for all subnets
 */
function PendingSubnetsOverview(props: PendingSubnetsOverviewProps) {
    const [t] = useTranslation("customer-subnets");

    const { page } = Route.useSearch();
    const router = useRouter();

    const data = Route.useLoaderData();
    if (!data) {
        return;
    }

    useEffect(() => {
        const listener = WS.addEventListener("message.SubnetsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-6"}>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.prefix")}</TableHeader>
                        <TableHeader>{t("label.device-router")}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((subnet) => (
                        <TableRow key={subnet.uuid}>
                            <TableCell>{subnet.subnet.name}</TableCell>
                            <TableCell>
                                <div className={"flex flex-col gap-1"}>
                                    <span>{subnet.device.dnsname}</span>
                                    <div className={"flex gap-2"}>
                                        {subnet.device.tags.map((tag) => (
                                            <TagBadge key={tag.uuid} tag={tag} />
                                        ))}
                                    </div>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/c/subnets/pending"}
                getSearchParams={(newPage: number) => ({ page: newPage })}
            />
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type PendingSubnetsOverviewSearch = {
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_customer/c/subnets/_subnets/pending")({
    component: PendingSubnetsOverview,
    validateSearch: (search: Record<string, unknown>): PendingSubnetsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return { page: page <= 0 ? 1 : page };
    },
    loaderDeps: ({ search: { page } }) => ({ page }),
    loader: async ({ deps }) =>
        await ApiCustomer.subnets.getPendingRoutes({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
        }),
});
